import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'

import { addCustomerModalInitialState, addCustomerModalReducer } from './addCustomerModalSlice'
import {
  approvalRemarkModalInitialState,
  approvalRemarkModalReducer,
  selectApprovalRemarkModal,
} from './approvalRemarkModalSlice'
import { printOfferModalInitialState, printOfferModalReducer } from './printOfferModal'
import {
  resubmitOfferModalInitialState,
  resubmitOfferModalReducer,
  selectResubmitOfferModal,
} from './resubmitOfferModalSlice'

export const initialState = {
  offers: [],
  status: 'idle',

  workingOfferViewingUsers: [],
  workingOfferEditingUserId: '',

  createTempVendorModal: {
    show: false,
  },
  adminEditOfferModal: {
    show: false,
    offerId: '',
  },
  reviseOfferModal: {
    show: false,
    offerId: '',
    projectName: '',
  },
  cloneOfferModal: {
    show: false,
    offerId: '',
    salesId: '',
    subSystemIndex: 0,
    isRenew: false,
  },
  createOfferModal: {
    show: false,
    offerType: '',
  },
  addSubSystemModal: {
    isOpened: false,
    isSubmitted: false,
    subSystemType: '',
    profitCenterId: '',
    subSystemName: '',
  },

  approvalInvitedUsers: [],
  ...resubmitOfferModalInitialState,
  ...approvalRemarkModalInitialState,
  ...printOfferModalInitialState,
  ...addCustomerModalInitialState,
}

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setWorkingOfferViewingUsers(state, { payload }) {
      state.workingOfferViewingUsers = payload
    },

    showCreateTempVendorModal(state) {
      state.createTempVendorModal = {
        show: true,
      }
    },
    hideCreateTempVendorModal(state) {
      state.createTempVendorModal = {
        show: false,
      }
    },
    showAdminEditOfferModal(state, { payload: { offerId } }) {
      state.adminEditOfferModal = {
        show: true,
        offerId: offerId,
      }
    },
    hideAdminEditOfferModal(state) {
      state.adminEditOfferModal = {
        show: false,
        offerId: '',
      }
    },
    showReviseOfferModal(state, { payload: { offerId, projectName } }) {
      state.reviseOfferModal = {
        show: true,
        offerId: offerId,
        projectName: projectName,
      }
    },
    hideReviseOfferModal(state) {
      state.reviseOfferModal = {
        show: false,
        offerId: '',
        projectName: '',
      }
    },
    showCloneOfferModal(state, { payload: { offerId, salesId, subSystemIndex, isRenew } }) {
      state.cloneOfferModal = {
        show: true,
        offerId: offerId,
        salesId: salesId,
        subSystemIndex: subSystemIndex,
        isRenew: isRenew,
      }
    },
    hideCloneOfferModal(state) {
      state.cloneOfferModal = {
        show: false,
        offerId: '',
        salesId: '',
        subSystemIndex: 0,
        isRenew: false,
      }
    },
    showCreateOfferModal(state, { payload: { offerType } }) {
      state.createOfferModal = {
        show: true,
        offerType: offerType,
      }
    },
    hideCreateOfferModal(state) {
      state.createOfferModal = {
        show: false,
        offerType: '',
      }
    },

    //
    openAddSubSystemModal(state) {
      state.addSubSystemModal.isOpened = true
      state.addSubSystemModal.isSubmitted = false
      state.addSubSystemModal.subSystemType = ''
      state.addSubSystemModal.subSystemName = ''
      state.addSubSystemModal.profitCenterId = ''
    },
    submitAddSubSystemModal(state, { payload }) {
      state.addSubSystemModal.isOpened = false
      state.addSubSystemModal.isSubmitted = true
      state.addSubSystemModal.subSystemType = payload?.subSystemType || ''
      state.addSubSystemModal.subSystemName = payload?.subSystemName || ''
      state.addSubSystemModal.profitCenterId = payload?.profitCenterId || ''
    },
    closeAddSubSystemModal(state) {
      state.addSubSystemModal.isOpened = false
      state.addSubSystemModal.isSubmitted = false
      state.addSubSystemModal.subSystemType = ''
      state.addSubSystemModal.subSystemName = ''
      state.addSubSystemModal.profitCenterId = ''
    },

    //  Invitation
    setApprovalInvitedUsers(state, { payload }) {
      state.approvalInvitedUsers = payload
    },
    ...resubmitOfferModalReducer,
    ...approvalRemarkModalReducer,
    ...printOfferModalReducer,
    ...addCustomerModalReducer,
  },
})
export const {
  setWorkingOfferViewingUsers,

  showCreateTempVendorModal,
  hideCreateTempVendorModal,
  showAdminEditOfferModal,
  hideAdminEditOfferModal,
  showReviseOfferModal,
  hideReviseOfferModal,
  showCloneOfferModal,
  hideCloneOfferModal,
  showCreateOfferModal,
  hideCreateOfferModal,
  openAddSubSystemModal,
  submitAddSubSystemModal,
  closeAddSubSystemModal,
  setApprovalInvitedUsers,
  showResubmitOfferModal,
  hideResubmitOfferModal,
  showApprovalRemarkModal,
  hideApprovalRemarkModal,
  showPrintOfferModal,
  hidePrintOfferModal,
  showAddCustomerModal,
  submitAddCustomerModal,
  hideAddCustomerModal,
} = offersSlice.actions

export const addSubSystemModalThunkAction = {
  open: createAsyncThunk('offer', async (payload, { extra, dispatch }) => {
    const store = extra.store

    dispatch(openAddSubSystemModal(payload))

    return new Promise((resolve) => {
      const unsubscribe = store.subscribe(() => {
        const state = store.getState()
        const { isSubmitted, isOpened, subSystemType, profitCenterId, subSystemName } = state.offers.addSubSystemModal
        if (isSubmitted) {
          unsubscribe()
          resolve({
            isSubmitted: true,
            subSystemType,
            profitCenterId,
            subSystemName,
          })
        }

        if (!isOpened) {
          unsubscribe()
          resolve({ isSubmitted: false })
        }
      })
    })
  }),
}

export const useAddSubSystemModalManagement = () => {
  const dispatch = useDispatch()
  const { isOpened } = useSelector((state) => state.offers.addSubSystemModal)

  const open = async () => {
    const { payload } = await dispatch(addSubSystemModalThunkAction.open())
    return payload
  }
  const submit = ({ subSystemType, profitCenterId, subSystemName }) => {
    return dispatch(submitAddSubSystemModal({ subSystemType, profitCenterId, subSystemName }))
  }
  const close = () => {
    return dispatch(closeAddSubSystemModal())
  }
  return {
    isOpened,
    open,
    submit,
    close,
  }
}

export const selectCreateTempVendorModal = (state) => state.offers.createTempVendorModal
export const selectAdminEditOfferModal = (state) => state.offers.adminEditOfferModal
export const selectReviseOfferModal = (state) => state.offers.reviseOfferModal
export const selectCloneOfferModal = (state) => state.offers.cloneOfferModal
export const selectCreateOfferModal = (state) => state.offers.createOfferModal

export const selectApprovalInvitedUsers = (state) => state.offers.approvalInvitedUsers

export { selectApprovalRemarkModal, selectResubmitOfferModal }

export default offersSlice.reducer
